import BigNumber from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';

import { getRoundedInputAmount } from '@/utils/getRoundedInputAmount';
import { useWallet } from '@/utils/useWallet.hook';
import { UserEarnDetailResponse } from '@utx/utx-client';

export interface SubscribeForm {
  offerId: string;
  walletId: string;
  subscriptionAmount: string;
  autoRenew?: boolean;
  isMax?: boolean;
}

interface UseSubscribeFormParams {
  offer?: UserEarnDetailResponse;
}

export const useSubscribeForm = ({ offer }: UseSubscribeFormParams) => {
  const [values, setValues] = useState<Partial<SubscribeForm>>({
    offerId: '',
    walletId: '',
    subscriptionAmount: undefined,
    autoRenew: false,
    isMax: false
  });
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    if (!offer) return;

    setValues((prevValue) => ({
      ...prevValue,
      offerId: offer.id
    }));

    return () => {
      setValues((prevValue) => ({
        ...prevValue,
        offerId: '',
        walletId: '',
        subscriptionAmount: undefined,
        autoRenew: false,
        isMax: false
      }));
    };
  }, [offer]);

  const wallet = useWallet({ noSave: true });

  const account = useMemo(() => {
    // return undefined;
    if (!offer?.asset) return;
    if (!wallet) return;
    const selectedAccount = wallet.getWalletById(offer.asset, values.walletId);
    return selectedAccount;
  }, [offer?.asset, values.walletId, wallet]);

  const handleValueChange = (key: keyof SubscribeForm, value: any) => {
    setValues((prevValue) => ({
      ...prevValue,
      [key]: value
    }));
  };

  const formatSubscriptionAmountOnBlur = (value?: number) => {
    if (value === undefined) return;

    const balance = new BigNumber(account?.balance || 0).toNumber();
    const amount = new BigNumber(value).toNumber();
    const minAmount = new BigNumber(
      offer?.minSubscriptionAmount === undefined ? offer?.lotSize || -Infinity : offer?.minSubscriptionAmount
    ).toNumber();

    const maxAmount = new BigNumber(offer?.maxSubscriptionAmount === undefined ? Infinity : offer.maxSubscriptionAmount).toNumber();

    const roundedInputAmount = getRoundedInputAmount({
      amount,
      balance,
      minAmount,
      maxAmount,
      stepSize: offer?.lotSize
    });

    return roundedInputAmount;
  };

  const error = useMemo(() => {
    setErrorText(null);
    if (!account) return undefined;
    if (values.subscriptionAmount === undefined) return undefined;
    // Amount Input
    if (new BigNumber(values.subscriptionAmount).gt(new BigNumber(account?.balance || 0))) {
      const errorMessage = 'earn-invest::glossary.insufficient-balance';
      setErrorText(errorMessage);
      return errorMessage;
    }

    // Check Min Max
    const min = new BigNumber(offer?.minSubscriptionAmount || -Infinity);
    const maxSubscriptionAmount = new BigNumber(offer?.maxSubscriptionAmount || Infinity);
    const walletBalance = new BigNumber(account?.balance || 0);
    const max = maxSubscriptionAmount.gt(walletBalance) ? walletBalance : maxSubscriptionAmount;
    const amount = new BigNumber(values.subscriptionAmount);

    const invalidInput = amount.lt(min) || amount.gt(max);
    if (invalidInput) {
      const errorMessage = walletBalance.lt(min)
        ? 'earn-invest::glossary.insufficient-balance'
        : 'earn-invest::glossary.invalid-subscription-amount';
      setErrorText(errorMessage);
      return errorMessage;
    }

    return undefined;
  }, [account, values.subscriptionAmount, values.walletId]);

  const validator = (value?: number | undefined) => {
    const check = () => {
      if (!account) return null;
      if (value === undefined) return null;
      // Amount Input
      if (new BigNumber(value).gt(new BigNumber(account?.balance || 0))) return 'earn-invest::glossary.insufficient-balance';

      // Check Min Max
      const min = new BigNumber(offer?.minSubscriptionAmount || -Infinity);
      const maxSubscriptionAmount = new BigNumber(offer?.maxSubscriptionAmount || Infinity);
      const walletBalance = new BigNumber(account?.balance || 0);
      const max = maxSubscriptionAmount.gt(walletBalance) ? walletBalance : maxSubscriptionAmount;
      const amount = new BigNumber(value);

      const invalidInput = amount.lt(min) || amount.gt(max);
      if (invalidInput) {
        const errorMessage = walletBalance.lt(min)
          ? 'earn-invest::glossary.insufficient-balance'
          : 'earn-invest::glossary.invalid-subscription-amount';
        setErrorText(errorMessage);
        return errorMessage;
      }

      return null;
    };
    const result = check();
    setErrorText(result);
  };

  const disableSubmit = useMemo(() => {
    const enableSubmit =
      !error && values.walletId && values.subscriptionAmount !== undefined && new BigNumber(values.subscriptionAmount).gt(0);
    return !enableSubmit;
  }, [values, error]);

  return {
    account,
    values,
    error,
    disableSubmit,
    errorText,
    validator,
    handleValueChange,
    formatSubscriptionAmountOnBlur
  };
};
